import type { WorkBook } from 'xlsx';

import {
  cellText,
  headerIndex,
  isNonzeroNumber,
  normalizeHeader,
} from './cells';
import {
  classifyRdSuspendedLocations,
  isRdSuspendedDivision,
  rdLocationLetterFromCell,
  rdSuspendedCorrespondenceFrom,
} from './rdSuspendedLocationMatch';
import { FlaggedSuspendedItems } from './flaggedSuspendedItems';
import type { FlagReason, FlaggedSuspendedRow } from './types';
import { sheetToAoa } from './workbookSheets';

export const SUSPENDED_ITEM_HEADERS = [
  'DIV',
  'BRANCH',
  'BUYER',
  'ITEM#',
  'DESCRIP',
  'WRH LOCATION',
  'OVERSTK WRH LOCATION',
  'QTY ON HAND',
  'STK Y/N',
  'REVISED DATE',
  'ON-ORD QTY',
  'ALLOC QTY',
  'B/O QTY',
  'P/O#',
  'POLN#',
  'ORDER NUMBER',
  'SOLN#',
  'CREATE DATE',
  'CRT PGM',
  'CRT USER',
  'REV PGM',
  'REV USER',
] as const;

const TEXT_FLAGS = [
  { header: 'WRH LOCATION', id: 'warehouseLocation' },
  { header: 'OVERSTK WRH LOCATION', id: 'overstockWarehouseLocation' },
] as const;

const NONZERO_FLAGS = [
  { header: 'QTY ON HAND', id: 'qtyOnHand' },
  { header: 'ON-ORD QTY', id: 'onOrderQty' },
  { header: 'ALLOC QTY', id: 'allocQty' },
  { header: 'B/O QTY', id: 'backorderQty' },
  { header: 'P/O#', id: 'poNumber' },
  { header: 'ORDER NUMBER', id: 'orderNumber' },
] as const;

/** Columns we flag on. Missing any of these would silently skip discrepancies. */
const ANALYSIS_HEADERS = [
  'ITEM#',
  'STK Y/N',
  ...TEXT_FLAGS.map((rule) => rule.header),
  ...NONZERO_FLAGS.map((rule) => rule.header),
] as const;

export interface SuspendedItemAnalysis {
  rowsScanned: number;
  flagged: FlaggedSuspendedRow[];
}

/** RD ITEMSUSPE4 (VL 2026-09-25) prints STOCK LOC / ITEM LOC, not BRANCH. */
export const RD_SUSPENDED_ITEM_HEADERS = [
  'DIV',
  'STOCK LOC',
  'ITEM LOC',
  ...SUSPENDED_ITEM_HEADERS.slice(2),
] as const;

/**
 * The AS/400 field names an ITEMSUSPE4 export prints when it is saved with
 * field names instead of column headings (the RA and RD `.xlsb` re-saves,
 * 2026-10-27), mapped to the heading in the same position.
 */
const SUSPENDED_ITEM_FIELD_HEADINGS: Readonly<Record<string, string>> = {
  SLOCX: 'BRANCH',
  ISLOCX: 'STOCK LOC',
  ILOCX: 'ITEM LOC',
  IBUYERX: 'BUYER',
  ITEMNOX: 'ITEM#',
  IEDESCX: 'DESCRIP',
  LOCATION: 'WRH LOCATION',
  OVERSTCK: 'OVERSTK WRH LOCATION',
  QTYONHAND: 'QTY ON HAND',
  STKYN: 'STK Y/N',
  RDATE: 'REVISED DATE',
  SQTYOOX: 'ON-ORD QTY',
  SQTYALX: 'ALLOC QTY',
  SQTYBOX: 'B/O QTY',
  'DPORD#': 'P/O#',
  POLNO: 'POLN#',
  DORDNO: 'ORDER NUMBER',
  SOLNO: 'SOLN#',
  CDATE: 'CREATE DATE',
  SCRPGM: 'CRT PGM',
  SCRUSR: 'CRT USER',
  SRVPGM: 'REV PGM',
  SRVUSR: 'REV USER',
};

function withHeadings(row: unknown[]): unknown[] {
  return row.map(
    (cell) => SUSPENDED_ITEM_FIELD_HEADINGS[normalizeHeader(cell)] ?? cell,
  );
}

function isSuspendedHeaderRow(row: unknown[]): boolean {
  return ANALYSIS_HEADERS.every((header) => headerIndex(row, header) !== -1);
}

type Cell = (header: string) => string;

/**
 * One sheet's header, resolved once: a field-name export runs to millions
 * of rows, so data rows must not re-normalize the header per cell.
 */
class SuspendedHeader {
  private readonly columns = new Map<string, number>();
  /** RD STOCK LOC / ITEM LOC layout rather than BRANCH / WRH LOCATION. */
  readonly stockItemColumns: boolean;

  constructor(private readonly headerRow: unknown[]) {
    this.stockItemColumns =
      this.column('STOCK LOC') !== -1 && this.column('ITEM LOC') !== -1;
  }

  private column(header: string): number {
    let index = this.columns.get(header);
    if (index === undefined) {
      index = headerIndex(this.headerRow, header);
      this.columns.set(header, index);
    }
    return index;
  }

  cells(row: unknown[]): Cell {
    return (header) => {
      const index = this.column(header);
      return index === -1 ? '' : cellText(row[index]);
    };
  }
}

function isRdBranchPairingWarehouse(
  header: SuspendedHeader,
  div: string,
  reasonId: (typeof TEXT_FLAGS)[number]['id'],
  value: string,
): boolean {
  if (reasonId !== 'warehouseLocation') return false;
  if (header.stockItemColumns) return false;
  if (!isRdSuspendedDivision(div)) return false;
  return rdLocationLetterFromCell(value) !== '';
}

function collectReasons(cell: Cell, header: SuspendedHeader): FlagReason[] {
  const reasons: FlagReason[] = [];
  for (const rule of TEXT_FLAGS) {
    const value = cell(rule.header);
    if (value === '') continue;
    if (isRdBranchPairingWarehouse(header, cell('DIV'), rule.id, value)) {
      continue;
    }
    reasons.push({ id: rule.id, column: rule.header, value });
  }
  const stockFlag = cell('STK Y/N');
  if (stockFlag.toUpperCase() === 'Y') {
    reasons.push({ id: 'stockFlagY', column: 'STK Y/N', value: stockFlag });
  }
  for (const rule of NONZERO_FLAGS) {
    const value = cell(rule.header);
    if (isNonzeroNumber(value)) {
      reasons.push({ id: rule.id, column: rule.header, value });
    }
  }
  return reasons;
}

function classifySuspendedLocation(cell: Cell, header: SuspendedHeader) {
  const stockItemColumns = header.stockItemColumns;
  return classifyRdSuspendedLocations({
    div: cell('DIV'),
    branch: stockItemColumns ? cell('STOCK LOC') : cell('BRANCH'),
    warehouseLocation: stockItemColumns
      ? cell('ITEM LOC')
      : cell('WRH LOCATION'),
    requireBothLetters: stockItemColumns,
  });
}

function rdVeraPairReason(
  location: ReturnType<typeof classifySuspendedLocation>,
): FlagReason | null {
  if (location.kind !== 'show') return null;
  return {
    id: 'rdVeraPair',
    column: 'STOCK LOC',
    value: `${location.match.stockLetter}↔${location.match.itemMasterLetter}`,
  };
}

function nonzeroValues(value: string): string[] {
  return isNonzeroNumber(value) ? [value] : [];
}

function toFlaggedRow(
  cell: Cell,
  header: SuspendedHeader,
  location: { fileName: string; sheetRow: number },
  reasons: FlagReason[],
  correspondence: {
    dc: string;
    stockLocation: string;
    itemMasterLocation: string;
  },
): FlaggedSuspendedRow {
  return {
    fileName: location.fileName,
    sheetRow: location.sheetRow,
    rowCount: 1,
    div: cell('DIV'),
    branch: header.stockItemColumns ? cell('STOCK LOC') : cell('BRANCH'),
    itemNumber: cell('ITEM#'),
    description: cell('DESCRIP'),
    warehouseLocation: cell('WRH LOCATION'),
    overstockWarehouseLocation: cell('OVERSTK WRH LOCATION'),
    qtyOnHand: cell('QTY ON HAND'),
    stockFlag: cell('STK Y/N'),
    onOrderQty: cell('ON-ORD QTY'),
    allocQty: cell('ALLOC QTY'),
    backorderQty: cell('B/O QTY'),
    poNumbers: nonzeroValues(cell('P/O#')),
    orderNumbers: nonzeroValues(cell('ORDER NUMBER')),
    reasons,
    dc: correspondence.dc,
    stockLocation: correspondence.stockLocation,
    itemMasterLocation: correspondence.itemMasterLocation,
  };
}

function decideSuspendedFlag(
  cell: Cell,
  header: SuspendedHeader,
  location: { fileName: string; sheetRow: number },
): FlaggedSuspendedRow | null {
  const reasons = collectReasons(cell, header);
  const match = classifySuspendedLocation(cell, header);
  if (match.kind === 'hide' || match.kind === 'unknown') return null;
  if (reasons.length === 0) return null;
  if (header.stockItemColumns) {
    const pair = rdVeraPairReason(match);
    if (pair) reasons.push(pair);
  }
  return toFlaggedRow(
    cell,
    header,
    location,
    reasons,
    rdSuspendedCorrespondenceFrom(match),
  );
}

/**
 * ITEMSUSPE4 fed a row at a time, so a streamed `.xlsb` and an in-memory
 * workbook share the analysis. Each sheet finds its own header (headings or
 * AS/400 field names); flagged rows fold into one per item across sheets.
 */
export class SuspendedItemScan {
  private sawHeader = false;
  private rowsScanned = 0;
  private readonly items = new FlaggedSuspendedItems();

  constructor(private readonly fileName: string) {}

  /** A row consumer for the next sheet. `rowIndex` is 0-based. */
  sheet(): (row: unknown[], rowIndex: number) => void {
    let header: SuspendedHeader | null = null;
    return (row, rowIndex) => {
      if (header === null) {
        const headerRow = withHeadings(row);
        if (isSuspendedHeaderRow(headerRow)) {
          header = new SuspendedHeader(headerRow);
          this.sawHeader = true;
        }
        return;
      }
      const cell = header.cells(row);
      if (cell('ITEM#') === '') return;
      this.rowsScanned += 1;
      const flaggedRow = decideSuspendedFlag(cell, header, {
        fileName: this.fileName,
        sheetRow: rowIndex + 1,
      });
      if (flaggedRow) this.items.add(flaggedRow);
    };
  }

  /** Null when no sheet printed the ITEMSUSPE4 header. */
  result(): SuspendedItemAnalysis | null {
    if (!this.sawHeader) return null;
    return { rowsScanned: this.rowsScanned, flagged: this.items.rows() };
  }
}

/**
 * Flag Suspended Item rows that still have location text, stock flag Y, or
 * a non-zero qty / open PO / open sales order. Returns null when no sheet
 * has the ITEMSUSPE4 header.
 *
 * Non-RD (RA, …) stays presence-only.
 *
 * RD BRANCH / WRH LOCATION (GEN-2668 Option A): presence flags only when
 * those cells are a Vera pair. Single-letter fallback stays on.
 *
 * RD STOCK LOC / ITEM LOC (GEN-2660, VL 2026-09-25; SUPP-361 Plan A):
 * both letters required; letter equality and cross-DC (O+H, blank+/C)
 * are hidden. Flag only when a presence reason exists; the pair is
 * correspondence, never a flag by itself.
 */
export function analyzeSuspendedItem(
  workbook: WorkBook,
  fileName: string,
): SuspendedItemAnalysis | null {
  const scan = new SuspendedItemScan(fileName);
  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    if (sheet) sheetToAoa(sheet).forEach(scan.sheet());
  }
  return scan.result();
}
